<template>
  <div class="hstack gap-2">
    <ul class="pagination mb-0">
      <li
        class="page-item"
        :class="{ disabled: currentPage === 1 }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(currentPage - 1)"
          aria-label="Previous"
        >
          <span aria-hidden="true">&laquo;</span>
        </a>
      </li>
      <li
        class="page-item"
        v-if="showStartPage"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(1)"
          >1</a
        >
      </li>
      <li
        class="page-item"
        v-if="showStartEllipsis"
        @click="gotoPage(currentPage - 5 < 1 ? 1 : currentPage - 5)"
      >
        <a
          class="page-link"
          href="#"
          >...</a
        >
      </li>
      <li
        class="page-item"
        v-for="page in visiblePages"
        :key="page"
        :class="{ active: currentPage === page }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(page)"
          >{{ page }}</a
        >
      </li>
      <li
        class="page-item"
        v-if="showEndEllipsis"
        @click="gotoPage(currentPage + 5 > totalPages ? totalPages : currentPage + 5)"
      >
        <a
          class="page-link"
          href="#"
          >...</a
        >
      </li>
      <li
        class="page-item"
        v-if="showEndPage"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(totalPages)"
          >{{ totalPages }}</a
        >
      </li>
      <li
        class="page-item"
        :class="{ disabled: currentPage === totalPages }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(currentPage + 1)"
          aria-label="Next"
        >
          <span aria-hidden="true">&raquo;</span>
        </a>
      </li>
    </ul>

    <select
      @change.self="changeOnPage"
      :value="onPage"
      class="form-select w-auto"
    >
      <option
        v-for="option in onPageArr"
        :key="option"
        :value="option"
      >
        {{ option }}
      </option>
    </select>

    <div
      class="input-group w-auto"
      v-if="totalPages > 2"
    >
      <input
        type="number"
        class="form-control"
        v-model="goToPageNumber"
        min="1"
        :max="totalPages"
        @keydown.enter.prevent="gotoPage(goToPageNumber)"
      />
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary rounded-end-3"
        @click="gotoPage(goToPageNumber)"
        :disabled="goToPageNumber < 1 || goToPageNumber > totalPages"
      >
        <i class="bi bi-chevron-double-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { isNil } from 'lodash-es'

const emit = defineEmits(['pageChange'])

const DEFAULT_ONPAGE = 20
const props = defineProps({
  totalItem: {
    type: Number,
    default: 1,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  visiblePageCount: {
    type: Number,
    default: 5,
  },
  onPageArr: {
    type: Array,
    default: () => [5, 10, 20, 50],
  },
  onPage: {
    type: Number,
    default: 20,
  },
})

const totalPagesView = ref([])
const goToPageNumber = ref('')

const gotoPage = (page, _onPage) => {
  let onPage = _onPage
  if (isNil(onPage)) {
    onPage = props.onPage
  }
  if (isNil(onPage)) {
    onPage = DEFAULT_ONPAGE
  }
  emit('pageChange', { page, onPage })
}

const totalPages = computed(() => {
  return Math.ceil(props.totalItem / props.onPage)
})

const startIndex = computed(() => {
  let start = props.currentPage - Math.ceil(props.visiblePageCount / 2)
  if (start < 0) start = 0
  else if (start > totalPagesView.value.length - props.visiblePageCount)
    start = totalPagesView.value.length - props.visiblePageCount
  return start
})

const endIndex = computed(() => {
  return Math.min(startIndex.value + props.visiblePageCount, totalPagesView.value.length)
})

const visiblePages = computed(() => {
  if (totalPagesView.value.length <= props.visiblePageCount) {
    return totalPagesView.value.length ? totalPagesView.value : [1]
  }
  return totalPagesView.value.slice(startIndex.value, endIndex.value)
})

const showStartPage = computed(() => {
  return startIndex.value > 0
})

const showStartEllipsis = computed(() => {
  return startIndex.value > 1
})

const showEndEllipsis = computed(() => {
  return endIndex.value < totalPagesView.value.length - 1
})

const showEndPage = computed(() => {
  return endIndex.value < totalPagesView.value.length
})

watch(
  totalPages,
  newVal => {
    const pages = []
    for (let i = 1; i <= newVal; i++) {
      pages.push(i)
    }
    totalPagesView.value = pages
  },
  { immediate: true }
)

const changeOnPage = ({ target }) => {
  gotoPage(1, target.value)
}
</script>
